import type { ReactElement } from 'react';
import { Pencil } from 'lucide-react';
import type { Persona } from '@xdocs/schemas';
import { Badge, Button, Card, cn } from '@xdocs/ui';
import { usePersonasT } from './usePersonasT.js';
import styles from './PersonaCard.module.css';

const AVATAR_SWATCH_COUNT = 6;

/** Deterministic (never random-per-render) swatch index from a persona's
 * stable `id` — purely decorative variety, no semantic meaning attached to
 * a given color (CLAUDE_DESIGN.md/29-styling precedent: `personaArtOpts`
 * mirrors this same 6-color palette, now `--color-avatar-1..6` tokens). */
function swatchIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return (hash % AVATAR_SWATCH_COUNT) + 1;
}

function narrationSampleOf(persona: Persona): string | null {
  const sample = (persona.styleJsonb as Record<string, unknown> | null)?.['narrationSample'];
  return typeof sample === 'string' && sample.trim().length > 0 ? sample : null;
}

/** design/app/24-personas.html's gallery card shows `{{ p.voice }}` as the
 * body paragraph (not a separate "description") — `styleJsonb.voice` is the
 * mockup-visible field; `persona.description` has no card-visible mockup
 * counterpart, so it's kept as a form-only field (still round-trips to the
 * backend) and falls back here only when a persona predates the voice field. */
function voiceOf(persona: Persona): string | null {
  const voice = (persona.styleJsonb as Record<string, unknown> | null)?.['voice'];
  if (typeof voice === 'string' && voice.trim().length > 0) return voice;
  return persona.description ?? null;
}

export interface PersonaCardProps {
  persona: Persona;
  /** `'mine'` shows Edit/Delete; `'public'` shows "Add to my personas". */
  scope: 'mine' | 'public';
  onEdit: (persona: Persona) => void;
  onDelete: (persona: Persona) => void;
  onImport: (persona: Persona) => void;
  onUseInGeneration: (persona: Persona) => void;
}

/** One `/personas` (F16) gallery tile, matching design/app/24-personas.html:
 * color swatch, name (+ edit pencil when it's mine), voice/tone blurb, an
 * italicized narration-sample quote, and a primary "Use in generation" CTA.
 * Delete / "Add to my personas" (public-tab import) live as secondary
 * actions below the CTA — real functionality the mockup's static frame
 * doesn't show a state for, but F16 requires (delete-confirm, importing a
 * public persona into your own set). */
export function PersonaCard({ persona, scope, onEdit, onDelete, onImport, onUseInGeneration }: PersonaCardProps): ReactElement {
  const { t } = usePersonasT();
  const sample = narrationSampleOf(persona);
  const voice = voiceOf(persona);
  const swatch = swatchIndex(persona.id);

  return (
    <Card className={cn(styles['card'])}>
      <div className={cn(styles['header'])}>
        <span className={cn(styles[`swatch${swatch}`])} aria-hidden="true" />
        <span className={cn(styles['name'])}>{persona.name}</span>
        {persona.isPublic && (
          <Badge tone="success" className={cn(styles['publicBadge'])}>
            {t('personas:visibility.public')}
          </Badge>
        )}
        {scope === 'mine' && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('personas:list.action.edit')}
            className={cn(styles['editButton'])}
            onClick={() => onEdit(persona)}
          >
            <Pencil size={14} aria-hidden="true" />
          </Button>
        )}
      </div>

      {voice && <p className={cn(styles['description'])}>{voice}</p>}

      {sample && <blockquote className={cn(styles['sample'])}>“{sample}”</blockquote>}

      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={() => onUseInGeneration(persona)} className={cn(styles['useButton'])}>
          {t('personas:action.useInGeneration')}
        </Button>
        {scope === 'mine' ? (
          <Button variant="ghost" size="sm" onClick={() => onDelete(persona)}>
            {t('personas:list.action.delete')}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => onImport(persona)}>
            {t('personas:list.action.import')}
          </Button>
        )}
      </div>
    </Card>
  );
}
